// create action creator for saving config message data
import { Dispatch } from "redux";
import { Action, ActionType } from "../actions";
import PluginService from "../../services/pluginService";
import { IDisclosureTrackingMapping, IPluginConfiguration } from "../../models/configuration/plugin/IConfiguration";
import EncompassService from "../../services/encompassService";
import { hasPluginConfigChanges } from "../../utils/pluginConfigChangeDetector";
import { syncDisclosureTrackingToPushback } from "./syncPushbackHelper";

const saveConfigDisclosureTrackingMappingData = (configId: string, updatedDisclosureTrackingMapping: IDisclosureTrackingMapping | null, token: string | null) => {

    return async (dispatch: Dispatch<Action>, getState: () => any) => {

        dispatch({ type: ActionType.SAVE_CONFIG_PLUGIN_DATA });

        try {

            const { data: pluginConfiguration, persistedData, pushbackSyncPending } = getState().configPlugInData;

            if (!pluginConfiguration) {
                throw new Error(" No PluginConfiguration available in state to save");
            }

            if (!updatedDisclosureTrackingMapping) {
                throw new Error("No Disclosure Tracking Mappings to save");
            }

            // Using the Plug In State Data update the Disclosure Tracking Mappings
            const updatedConfig = {
                ...pluginConfiguration.Configuration,
                DisclosureTrackingMapping: updatedDisclosureTrackingMapping
            };

            // Check if Plugin has changes
            const hasPluginChanges = hasPluginConfigChanges(updatedConfig, persistedData?.Configuration);

            // If no plugin changes AND no pending pushback sync, nothing to do
            if (!hasPluginChanges && !pushbackSyncPending) {
                dispatch({
                    type: ActionType.SAVE_CONFIG_PLUGIN_DATA_SUCCESS,
                    payload: { "Configuration": updatedConfig }
                });
                return false;
            }

            // 1. Save to Plugin API (only if Plugin has changes)
            if (hasPluginChanges) {
                const configToSave = {
                    ...updatedConfig,
                    LastUpdateUtc: new Date().toISOString()
                };

                const requestBody = {
                    "ConfigurationId": configId,
                    "EncompassInstanceId": EncompassService.getEncompassInstanceId(),
                    "Configuration": configToSave
                };

                await new PluginService().saveEpcApiConfig<IPluginConfiguration>(token, "plugin", configId, requestBody);

                dispatch({
                    type: ActionType.SAVE_CONFIG_PLUGIN_DATA_SUCCESS,
                    payload: { "Configuration": configToSave }
                });
                dispatch({
                    type: ActionType.SAVE_CONFIG_PLUGIN_DATA_PERSISTED,
                    payload: { "Configuration": configToSave }
                });
            }

            // 2. Sync Disclosure Tracking Mapping to Pushback API
            // Always sync if: Plugin just saved OR previous Pushback sync failed
            if (hasPluginChanges || pushbackSyncPending) {
                const syncSucceeded = await syncDisclosureTrackingToPushback(
                    dispatch,
                    token,
                    configId,
                    updatedDisclosureTrackingMapping
                );

                // If sync failed, return true to indicate Plugin saved but retry needed
                if (!syncSucceeded) {
                    return true;
                }
            }
            return true;

        } catch (error: any) {
            const payloadErrorMessage = `Failed to save Messages data: (${error.message})`;
            dispatch({
                type: ActionType.SAVE_CONFIG_PLUGIN_DATA_FAILURE,
                payload: payloadErrorMessage,
            });

            return Promise.reject(payloadErrorMessage);
        }
    }
}

export default saveConfigDisclosureTrackingMappingData;